import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Avatar,
  Container,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import {
  getDatabaseSizes,
  getAllTenantsStorageSize,
  getTenantFolderSize,
  getAllBackups,
} from "../../../Services/BackupAndStorageApi";
import DownloadBackupTap from "./DownloadBackupTap";
import BackupIcon from "@mui/icons-material/Backup";
import StorageIcon from "@mui/icons-material/Storage";
import FolderIcon from "@mui/icons-material/Folder";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DatabaseIcon from "@mui/icons-material/Dataset";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SortIcon from "@mui/icons-material/Sort";

const BackupMain = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0); // State for active tab
  const [folderDetails, setFolderDetails] = useState(null); // State for folder details
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [backups, setBackups] = useState([]); // State for backups
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("default"); // default, asc, desc

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setFolderDetails(null); // Reset folder details when switching tabs
  };

  const handleFolderClick = async (tenantId) => {
    try {
      setLoading(true);

      const response = await getTenantFolderSize(tenantId);

      let folderObj = null;

      // لو API رجع Array فيها String
      if (Array.isArray(response) && typeof response[0] === "string") {
        folderObj = JSON.parse(response[0]);
      }
      // لو رجع String مباشرة
      else if (typeof response === "string") {
        folderObj = JSON.parse(response);
      }
      // لو رجع Object عادي
      else {
        folderObj = response;
      }

      setFolderDetails(folderObj); // ✅ خزن البيانات كـ Object
      setDialogOpen(true); // افتح الـ Dialog
    } catch (err) {
      console.error("Error fetching folder details:", err);
      setError("فشل في جلب تفاصيل المجلد");
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFolderDetails(null); // Reset folder details when dialog is closed
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // دالة لتحويل الحجم إلى رقم للمقارنة
  const parseSizeToMB = (sizeStr) => {
    if (!sizeStr || sizeStr === "-") return 0;
    const match = sizeStr.match(/(\d+\.?\d*)\s*(MB|GB|KB)/i);
    if (!match) return 0;

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    if (unit === "GB") return value * 1024;
    if (unit === "KB") return value / 1024;
    return value; // MB
  };

  // دالة فلترة البيانات
  const filterAndSortData = (dataArray) => {
    // البحث
    let filtered = dataArray.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // الترتيب حسب الحجم
    if (sortOrder === "asc") {
      filtered.sort((a, b) => parseSizeToMB(a.size) - parseSizeToMB(b.size));
    } else if (sortOrder === "desc") {
      filtered.sort((a, b) => parseSizeToMB(b.size) - parseSizeToMB(a.size));
    }

    return filtered;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [dbRes, tenantRes] = await Promise.all([
          getDatabaseSizes(),
          getAllTenantsStorageSize(),
        ]);

        console.log("dbRes =>", dbRes); // 🔎 شوف شكل البيانات
        console.log("tenantRes =>", tenantRes);

        const dbArray = Array.isArray(dbRes) ? dbRes : dbRes.data || []; // 👈 خليه يقرأ من .data لو مش Array

        const tenantArray = Array.isArray(tenantRes)
          ? tenantRes
          : tenantRes.data || [];

        const dbData = dbArray.map((db) => ({
          name: db.database_name,
          size: `${db.size_mb} MB`,
          status: "✅ قاعدة بيانات",
        }));

        const tenantData = tenantArray.map((t) => ({
          name: t.tenant_id,
          size: t.body?.size_mb ? `${t.body.size_mb} MB` : "-",
          status:
            t.status === 200
              ? "✅ مجلد موجود"
              : `❌ ${t.body?.message || "خطأ"}`,
        }));

        setData([...dbData, ...tenantData]);
      } catch (err) {
        console.error("Error fetching backup data:", err);
        setError("فشل في جلب بيانات النسخ الاحتياطي");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchBackups = async () => {
      try {
        const response = await getAllBackups();
        if (response.status === "success") {
          const backupArray = Object.entries(response.backups).map(
            ([id, name]) => ({ id, name })
          );
          setBackups(backupArray);
        }
      } catch (err) {
        console.error("Error fetching backups:", err);
        setError("فشل في جلب النسخ الاحتياطية");
      }
    };

    if (tabIndex === 2) fetchBackups(); // Fetch backups only when the tab is active
  }, [tabIndex]);

  const dbData = filterAndSortData(data.filter((item) => item.status === "✅ قاعدة بيانات"));
  const folderData = filterAndSortData(data.filter((item) => item.status !== "✅ قاعدة بيانات"));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Card elevation={3} sx={{ mb: 3, background: 'linear-gradient(135deg, #43cea2 0%, #185a9d 100%)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#185a9d', width: 50, height: 50 }}>
              <BackupIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                إدارة النسخ الاحتياطي والتخزين
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                قواعد البيانات، المجلدات، والنسخ الاحتياطية
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>جاري تحميل البيانات...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ fontSize: '1.1rem', py: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <Tabs 
              value={tabIndex} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                bgcolor: '#f5f5f5',
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  minHeight: 72,
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: '#e3f2fd',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'white',
                    color: '#1976d2',
                  }
                }
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DatabaseIcon />
                    قواعد البيانات
                    <Chip label={dbData.length} size="small" color="primary" />
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FolderIcon />
                    المجلدات
                    <Chip label={folderData.length} size="small" color="secondary" />
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudDownloadIcon />
                    تحميل النسخ الاحتياطية
                  </Box>
                }
              />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* شريط البحث والفلتر */}
              {(tabIndex === 0 || tabIndex === 1) && data.length > 0 && (
                <>
                  {/* شريط البحث */}
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      mb: 2, 
                      bgcolor: '#f8f9fa',
                      border: '2px solid #e3f2fd'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#1976d2' }}>
                          <SearchIcon />
                        </Avatar>
                        <TextField
                          fullWidth
                          placeholder={`ابحث في ${tabIndex === 0 ? 'قواعد البيانات' : 'المجلدات'}...`}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          variant="outlined"
                          sx={{ 
                            bgcolor: 'white',
                            '& .MuiOutlinedInput-root': {
                              '&:hover fieldset': {
                                borderColor: '#1976d2',
                              },
                            }
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="primary" />
                              </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                              <InputAdornment position="end">
                                <Tooltip title="مسح البحث">
                                  <IconButton
                                    onClick={handleClearSearch}
                                    edge="end"
                                    size="small"
                                    sx={{ 
                                      bgcolor: '#ffebee',
                                      '&:hover': {
                                        bgcolor: '#ffcdd2'
                                      }
                                    }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>

                  {/* فلتر الترتيب */}
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      mb: 3, 
                      bgcolor: '#fff3e0',
                      border: '2px solid #ffe0b2'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Avatar sx={{ bgcolor: '#ff9800' }}>
                          <SortIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                          ترتيب حسب الحجم:
                        </Typography>
                        <Box sx={{ minWidth: 250 }}>
                          <FormControl fullWidth size="small" sx={{ bgcolor: 'white' }}>
                            <InputLabel>اختر الترتيب</InputLabel>
                            <Select
                              value={sortOrder}
                              label="اختر الترتيب"
                              onChange={(e) => setSortOrder(e.target.value)}
                            >
                              <MenuItem value="default">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <SortIcon fontSize="small" />
                                  الترتيب الافتراضي
                                </Box>
                              </MenuItem>
                              <MenuItem value="asc">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <SortIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                                  من الأصغر إلى الأكبر
                                </Box>
                              </MenuItem>
                              <MenuItem value="desc">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <SortIcon fontSize="small" />
                                  من الأكبر إلى الأصغر
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                        {(searchQuery || sortOrder !== "default") && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
                            <Chip 
                              label={`${tabIndex === 0 ? dbData.length : folderData.length} نتيجة`}
                              color="warning"
                              sx={{ fontWeight: 'bold' }}
                            />
                            <Tooltip title="إعادة تعيين">
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<ClearIcon />}
                                onClick={() => {
                                  setSearchQuery("");
                                  setSortOrder("default");
                                }}
                                sx={{ fontWeight: 'bold' }}
                              >
                                إعادة تعيين
                              </Button>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </>
              )}

              {tabIndex === 0 && (
                <Box sx={{ 
                  animation: 'fadeIn 0.5s',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}>
                  {dbData.length > 0 ? (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم قاعدة البيانات</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>الحجم</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>الحالة</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {dbData.map((row, idx) => (
                            <TableRow 
                              key={idx}
                              sx={{ '&:hover': { bgcolor: '#f9f9f9', transition: 'all 0.2s' } }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ bgcolor: '#2196f3', width: 32, height: 32 }}>
                                    <DatabaseIcon fontSize="small" />
                                  </Avatar>
                                  <Typography sx={{ fontWeight: 'bold' }}>
                                    {row.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  icon={<StorageIcon />}
                                  label={row.size}
                                  color="primary"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={<CheckCircleIcon />}
                                  label="نشطة"
                                  color="success"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff3e0', borderRadius: 2, border: '2px dashed #ff9800' }}>
                      <SearchIcon sx={{ fontSize: 80, color: '#ff9800', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        لا توجد نتائج للبحث
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        جرب تغيير كلمات البحث
                      </Typography>
                      {searchQuery && (
                        <Chip 
                          label={`البحث: "${searchQuery}"`}
                          color="warning"
                          onDelete={handleClearSearch}
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {tabIndex === 1 && (
                <Box sx={{ 
                  animation: 'fadeIn 0.5s',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}>
                  {folderData.length > 0 ? (
                    <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>اسم المجلد</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>الحجم</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1rem' }}>الحالة</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {folderData.map((row, idx) => (
                            <TableRow 
                              key={idx}
                              sx={{ '&:hover': { bgcolor: '#f9f9f9', transition: 'all 0.2s' } }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar sx={{ bgcolor: '#9c27b0', width: 32, height: 32 }}>
                                    <FolderIcon fontSize="small" />
                                  </Avatar>
                                  <Typography sx={{ fontWeight: 'bold' }}>
                                    {row.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  icon={<StorageIcon />}
                                  label={row.size}
                                  color="secondary"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={row.status.includes('✅') ? <CheckCircleIcon /> : <ErrorOutlineIcon />}
                                  label={row.status.includes('✅') ? 'موجود' : 'خطأ'}
                                  color={row.status.includes('✅') ? 'success' : 'error'}
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              </TableCell>
                              
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff3e0', borderRadius: 2, border: '2px dashed #ff9800' }}>
                      <SearchIcon sx={{ fontSize: 80, color: '#ff9800', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        لا توجد نتائج للبحث
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        جرب تغيير كلمات البحث
                      </Typography>
                      {searchQuery && (
                        <Chip 
                          label={`البحث: "${searchQuery}"`}
                          color="warning"
                          onDelete={handleClearSearch}
                          sx={{ fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              )}

              {tabIndex === 2 && (
                <Box sx={{ 
                  animation: 'fadeIn 0.5s',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}>
                  <DownloadBackupTap />
                </Box>
              )}
            </Box>
          </Card>
        </>
      )}

      {/* Dialog for folder details */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#9c27b0', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'white', color: '#9c27b0', width: 40, height: 40 }}>
              <FolderIcon />
            </Avatar>
            تفاصيل المجلد
          </Box>
          <IconButton
            onClick={handleDialogClose}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {folderDetails ? (
            <Box>
              <Card variant="outlined" sx={{ mb: 2, bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      الحالة:
                    </Typography>
                    <Chip label={folderDetails.status} color="success" size="small" />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FolderIcon sx={{ color: '#9c27b0' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      التينانت:
                    </Typography>
                    <Chip label={folderDetails.tenant} color="secondary" size="small" />
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImageIcon sx={{ color: '#2196f3' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      حجم الوسائط:
                    </Typography>
                    <Chip 
                      icon={<StorageIcon />}
                      label={folderDetails.media_size} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
              <CircularProgress size={30} />
              <Typography>جاري تحميل التفاصيل...</Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, bgcolor: '#f5f5f5' }}>
          <Button 
            onClick={handleDialogClose} 
            variant="contained"
            size="large"
            sx={{ minWidth: 100 }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BackupMain;
